// @ts-nocheck
import { createEffect, onMount, onCleanup, createSignal } from "solid-js";
import type mapboxgl from "mapbox-gl";
import { streamingStore } from "../../../../store/streamingStore";
import { sourcesStore } from "../../../../store/sourcesStore";
import { selectedTime, isPlaying, timeWindow, smoothPlaybackTimeForTrack, smoothPlaybackTimeForBoat, isLiveReplayPlayheadReady } from "../../../../store/playbackStore";
import { selectedRange, selectedRanges } from "../../../../store/selectionStore";
import { resolveTrackColor } from "../renderers/boatMarkerStyle";
import { getInterpolatedPointAtTime, splitTrackPolylineAtGaps } from "../../../../utils/trackInterpolation";
import {
  appendCatalogSamplesToReplayTrail,
  buildDensifiedReplayTrack,
  LIVE_REPLAY_TRACK_STEP_MS,
  pruneReplayTrailToWindow,
  replayTrailRenderSignature,
  replayTrailRenderSignatureWithPlayhead,
  sliceReplayRawTrack,
} from "../../../../utils/liveReplayTrackPath";
import { debug, warn } from "../../../../utils/console";
import { liveReplayFilteredDataOptions } from "../../../../utils/liveReplayFilteredData";
import { type LiveReplaySourceStyle } from "../../../../utils/liveReplayFramePoints";

const SPLIT_VIEW_THROTTLE_MS = 150;
/** Throttle live track D3 redraws (~10fps) so WebSocket batches do not lock the main thread */
const LIVE_TRACK_THROTTLE_MS = 100;
/** Replay live map: track redraws follow the slow history/store refresh, not boat clock. */
const LIVE_TRACK_REPLAY_THROTTLE_MS = 2000;
/** Cap D3 line points per source — ~10 min @ 5 Hz track render grid; keep-newest decimation drops oldest. */
const LIVE_TRACK_REPLAY_MAX_POINTS = 3600;
/** Reject replay track segments that imply impossible boat speed; prevents late stale GPS branches folding into the trail. */
const LIVE_TRACK_MAX_JUMP_SPEED_KPH = 150;
const LIVE_TRACK_JUMP_MARGIN_M = 30;

/** Cap point count by dropping the OLDEST points; the newest tail is kept at full resolution. */
function decimateTrackPoints(points: any[], maxPoints: number): any[] {
  if (points.length <= maxPoints) return points;
  return points.slice(points.length - maxPoints);
}

export interface LiveTrackLayerProps {
  map: mapboxgl.Map;
  selectedSourceIds: Set<number>;
  onPointClick?: (p: any) => void;
  pointSizePixels?: number; // Point size in pixels (default: 4)
  pointLineWidth?: number; // Line width of square outline in pixels (default: 1)
  historicalData?: any[]; // Historical data from MultiMapTimeSeries (IndexedDB query results)
  /** When provided (time-window playback), use this so track ends at boat position */
  effectivePlaybackTime?: Date | null;
  /** When true, throttle incremental updates to avoid stall with two map instances in split view */
  inSplitView?: boolean;
  /** Ignore global brush/selectionStore; clip tracks to playback window only. */
  replayMode?: boolean;
  /** Replay live map: shared per-source interpolated point from LiveMapContainer. */
  framePoints?: Map<number, unknown>;
  /** Replay live map: memoized sourceId → name/color. */
  sourceStyles?: Map<number, LiveReplaySourceStyle>;
  /** Rolling window minutes for track body (ignores cross-window timeWindow=0 sync). */
  replayTimeWindowMin?: number;
}

export default function LiveTrackLayer(props: LiveTrackLayerProps) {
  let d3: typeof import('d3') | null = null;
  
  let svg: any = null;
  let trackOverlay: any = null;
  let overlayContainer: HTMLElement | null = null;
  // Use signals to track SVG/trackOverlay state so effects can react to changes
  const [svgReady, setSvgReady] = createSignal(false);
  const [trackOverlayReady, setTrackOverlayReady] = createSignal(false);
  const [incrementalThrottleEpoch, setIncrementalThrottleEpoch] = createSignal(0);
  let hasInitialized = false; // Track if we've done initial load
  let lastRedisLoadSourceKey = ''; // Avoid calling loadInitialDataFromRedis in a loop when the history endpoint returns empty
  /** Stable key for the selection we finished initial load for (do not derive from lastProcessedTimestamps — that map omits sources with no points). */
  let lastSelectedSourceIdsKey = '';
  let lastProcessedTimestamps = new Map<number, number>(); // Track last processed timestamp per source

  const sourceSelectionKey = (ids: Set<number>) =>
    Array.from(ids)
      .filter((id) => Number.isFinite(id))
      .sort((a, b) => a - b)
      .join(',');

  const commitInitialLoadComplete = (sel: Set<number>) => {
    hasInitialized = true;
    lastSelectedSourceIdsKey = sourceSelectionKey(sel);
  };
  let mapUpdateHandler: (() => void) | null = null; // Store map update handler for cleanup
  let lastIncrementalUpdateTime = 0;
  let incrementalThrottleTimer: ReturnType<typeof setTimeout> | null = null;
  let lastIncrementalRenderSignature = '';
  /** Latest polyline per source — map pan/zoom reads this, not stale D3 datum. */
  let lastRenderedTrackBySourceId = new Map<number, any[]>();
  /** Skip replay D3 path updates when incremental trail fingerprint is unchanged. */
  let lastReplayTrailSignature = '';
  /** Per-source append-only replay trails (pruned by rolling window). */
  let replayTrailBySource = new Map<number, any[]>();
  /** Next catalog index consumed per source for incremental ingest. */
  let replayCatalogIndexBySource = new Map<number, number>();
  /**
   * Per-source cache of the coordinate-valid track catalog, keyed by the raw catalog length it
   * was computed at. The raw catalog is append-only, so on the common tick (no new history points)
   * we reuse the cached filtered array; when it grows we only validate the new tail. Without this,
   * the replay redraw re-filtered the entire (session-long) catalog every boat tick (~5 Hz).
   */
  let replayFilteredCatalogBySource = new Map<number, { rawLen: number; filtered: any[] }>();
  let lastReplaySourcesKey = '';
  const GAP_THRESHOLD_MS = 10000; // 10 seconds

  // Helper to get timestamp from data point
  const getTimestamp = (d: any): number => {
    if (!d) return 0;
    const timestamp = d.timestamp || (d.Datetime instanceof Date ? d.Datetime.getTime() : new Date(d.Datetime).getTime());
    return Number.isFinite(timestamp) ? timestamp : 0;
  };

  // Helper to get coordinates from a point (checks all possible field names)
  const getCoordinates = (point: any): { lng: number; lat: number } | null => {
    if (!point) return null;
    
    // Try normalized names first (Lat/Lng)
    let lng = point.Lng ?? point.lng;
    let lat = point.Lat ?? point.lat;
    
    // If not found, try default channel names (Lat_dd/Lng_dd)
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
      try {
        const { defaultChannelsStore } = require('../../../../store/defaultChannelsStore');
        if (defaultChannelsStore.isReady()) {
          const lngName = defaultChannelsStore.lngName();
          const latName = defaultChannelsStore.latName();
          if (!Number.isFinite(lng)) lng = point[lngName];
          if (!Number.isFinite(lat)) lat = point[latName];
        }
      } catch (e) {
        // Store not available, continue with normalized names
      }
    }
    
    // Also check direct default channel names (Lat_dd/Lng_dd) as fallback
    if (!Number.isFinite(lng)) lng = point.Lng_dd ?? point.lng_dd;
    if (!Number.isFinite(lat)) lat = point.Lat_dd ?? point.lat_dd;
    
    // Also check InfluxDB field names as fallback
    if (!Number.isFinite(lng)) lng = point.LONGITUDE_GPS_unk ?? point.longitude_gps_unk;
    if (!Number.isFinite(lat)) lat = point.LATITUDE_GPS_unk ?? point.latitude_gps_unk;
    
    if (Number.isFinite(lng) && Number.isFinite(lat)) {
      // Reject null-island sentinels — they draw spokes across the map
      if (Math.abs(lat) < 1e-6 && Math.abs(lng) < 1e-6) return null;
      return { lng, lat };
    }
    
    return null;
  };

  const distanceMeters = (
    a: { lat: number; lng: number },
    b: { lat: number; lng: number }
  ): number => {
    const radiusM = 6371000;
    const toRad = (deg: number) => deg * Math.PI / 180;
    const dLat = toRad(b.lat - a.lat);
    const dLng = toRad(b.lng - a.lng);
    const lat1 = toRad(a.lat);
    const lat2 = toRad(b.lat);
    const h =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
    return 2 * radiusM * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
  };

  const keepPlausibleReplayTrackPoints = (points: any[], sourceId: number): any[] => {
    if (points.length <= 1) return points;

    const kept: any[] = [];
    let rejected = 0;
    for (const point of points) {
      const ts = getTimestamp(point);
      const coords = getCoordinates(point);
      if (ts <= 0 || !coords) continue;

      const last = kept[kept.length - 1];
      if (!last) {
        kept.push(point);
        continue;
      }

      const lastTs = getTimestamp(last);
      const lastCoords = getCoordinates(last);
      if (!lastCoords || ts <= lastTs) {
        rejected++;
        continue;
      }

      const elapsedSec = Math.max(0.001, (ts - lastTs) / 1000);
      const distance = distanceMeters(lastCoords, coords);
      const maxDistance = LIVE_TRACK_JUMP_MARGIN_M + (LIVE_TRACK_MAX_JUMP_SPEED_KPH / 3.6) * elapsedSec;
      if (distance <= maxDistance) {
        kept.push(point);
      } else {
        rejected++;
      }
    }

    if (rejected > 0) {
      debug("[LiveTrackLayer] Rejected implausible replay track points", {
        sourceId,
        rejected,
        kept: kept.length,
      });
    }

    return kept;
  };

  const isPlausibleTrailAppend = (trail: any[], point: any): boolean => {
    if (trail.length === 0) return true;
    const ts = getTimestamp(point);
    const last = trail[trail.length - 1];
    const lastTs = getTimestamp(last);
    if (ts <= lastTs) return false;

    const lastCoords = getCoordinates(last);
    const coords = getCoordinates(point);
    if (!lastCoords || !coords) return true;

    const elapsedSec = Math.max(0.001, (ts - lastTs) / 1000);
    const distance = distanceMeters(lastCoords, coords);
    const maxDistance =
      LIVE_TRACK_JUMP_MARGIN_M + (LIVE_TRACK_MAX_JUMP_SPEED_KPH / 3.6) * elapsedSec;
    return distance <= maxDistance;
  };

  const resetReplayTrailCaches = () => {
    replayTrailBySource.clear();
    replayCatalogIndexBySource.clear();
    replayFilteredCatalogBySource.clear();
    lastReplayTrailSignature = '';
    lastReplaySourcesKey = '';
  };

  /**
   * Coordinate-valid catalog for a source, filtered incrementally. Append-only raw catalog means
   * an unchanged length can reuse the cached array; a grown length only validates the new tail.
   */
  const getValidCatalog = (sourceId: number, catalogRaw: any[]): any[] => {
    const entry = replayFilteredCatalogBySource.get(sourceId);
    if (!entry || entry.rawLen > catalogRaw.length) {
      // First sight, or catalog shrank/reset (e.g. a history reset) — rebuild from scratch.
      const filtered = catalogRaw.filter((p) => hasValidCoordinates(p));
      replayFilteredCatalogBySource.set(sourceId, { rawLen: catalogRaw.length, filtered });
      return filtered;
    }
    if (catalogRaw.length > entry.rawLen) {
      for (let i = entry.rawLen; i < catalogRaw.length; i++) {
        if (hasValidCoordinates(catalogRaw[i])) entry.filtered.push(catalogRaw[i]);
      }
      entry.rawLen = catalogRaw.length;
    }
    return entry.filtered;
  };

  const capReplayTrailLength = (trail: any[]): any[] => {
    if (trail.length <= LIVE_TRACK_REPLAY_MAX_POINTS) return trail;
    return decimateTrackPoints(trail, LIVE_TRACK_REPLAY_MAX_POINTS);
  };

  // Getters for track interpolation (live data shape)
  const getLatLive = (d: any): number | undefined => getCoordinates(d)?.lat;
  const getLngLive = (d: any): number | undefined => getCoordinates(d)?.lng;
  const liveInterpolateOptions = {
    getTimestamp: (d: any) => new Date(getTimestamp(d)),
    getLat: getLatLive,
    getLng: getLngLive,
    assumeSorted: true,
    noExtrapolate: true,
  };

  /** Extend windowed track to playhead; replay uses full store history for bracketing. */
  const buildTrackPathToPlayhead = (
    validPoints: any[],
    catalogPoints: any[],
    targetTime: Date | null | undefined
  ): any[] => {
    if (!(targetTime instanceof Date) || catalogPoints.length === 0) {
      return validPoints;
    }
    const targetMs = targetTime.getTime();
    const endpoint = getInterpolatedPointAtTime(catalogPoints, targetTime, liveInterpolateOptions);
    if (!endpoint || !hasValidCoordinates(endpoint)) {
      return validPoints.filter((p) => getTimestamp(p) <= targetMs);
    }
    const endTs = getTimestamp(endpoint);
    let body = validPoints.filter((p) => getTimestamp(p) < endTs);
    if (body.length === 0 && validPoints.length > 0) {
      body = validPoints.slice(0, -1);
    }
    if (body.length === 0) {
      return [endpoint];
    }
    return [...body, endpoint];
  };

  /**
   * Replay live map: maintain per-source incremental trails; update D3 paths only when trail changes.
   */
  const renderReplayTracksFromBoatClock = (
    selected: Set<number>,
    playhead: Date,
    histVer: number,
    trackVer: number
  ) => {
    if (!trackOverlay || !svg) return;

    const playheadMs = playhead.getTime();
    if (!isLiveReplayPlayheadReady(playheadMs)) return;

    const windowMin = props.replayTimeWindowMin ?? 0;
    const windowStartMs =
      windowMin > 0 ? playheadMs - windowMin * 60 * 1000 : playheadMs - 2 * 60 * 1000;
    const rawCatalogMap = streamingStore.getLiveTrackData(selected, { shallow: true });

    const sourcesKey = sourceSelectionKey(selected);
    if (sourcesKey !== lastReplaySourcesKey) {
      resetReplayTrailCaches();
      lastReplaySourcesKey = sourcesKey;
    }

    for (const sourceId of selected) {
      const catalogRaw = rawCatalogMap.get(sourceId) ?? [];
      const catalog = getValidCatalog(sourceId, catalogRaw);
      if (catalog.length === 0) {
        replayTrailBySource.delete(sourceId);
        replayCatalogIndexBySource.delete(sourceId);
        continue;
      }

      let trail = replayTrailBySource.get(sourceId);
      let catalogIndex = replayCatalogIndexBySource.get(sourceId) ?? 0;

      if (!trail) {
        const seed = keepPlausibleReplayTrackPoints(
          sliceReplayRawTrack(
            catalog,
            windowStartMs,
            playheadMs,
            getTimestamp,
            getCoordinates
          ) as any[],
          sourceId
        );
        trail = capReplayTrailLength(seed);
        catalogIndex = catalog.length;
        replayTrailBySource.set(sourceId, trail);
        replayCatalogIndexBySource.set(sourceId, catalogIndex);
      } else if (catalog.length < catalogIndex) {
        const reseed = keepPlausibleReplayTrackPoints(
          sliceReplayRawTrack(
            catalog,
            windowStartMs,
            playheadMs,
            getTimestamp,
            getCoordinates
          ) as any[],
          sourceId
        );
        trail = capReplayTrailLength(reseed);
        catalogIndex = catalog.length;
        replayTrailBySource.set(sourceId, trail);
        replayCatalogIndexBySource.set(sourceId, catalogIndex);
      } else if (catalog.length > catalogIndex) {
        const ingested = appendCatalogSamplesToReplayTrail(
          trail,
          catalog,
          catalogIndex,
          playheadMs,
          windowStartMs,
          getTimestamp,
          getCoordinates,
          (currentTrail, point) => isPlausibleTrailAppend(currentTrail as any[], point as any)
        );
        trail = capReplayTrailLength(ingested.trail as any[]);
        catalogIndex = ingested.nextCatalogIndex;
        replayTrailBySource.set(sourceId, trail);
        replayCatalogIndexBySource.set(sourceId, catalogIndex);
      }

      const pruned = capReplayTrailLength(
        pruneReplayTrailToWindow(trail, windowStartMs, getTimestamp) as any[]
      );
      if (pruned.length !== trail.length) {
        replayTrailBySource.set(sourceId, pruned);
      }
    }

    for (const sourceId of [...replayTrailBySource.keys()]) {
      if (!selected.has(sourceId)) {
        replayTrailBySource.delete(sourceId);
        replayCatalogIndexBySource.delete(sourceId);
        replayFilteredCatalogBySource.delete(sourceId);
      }
    }

    const trailFingerprint = replayTrailRenderSignatureWithPlayhead(
      selected,
      replayTrailBySource,
      getTimestamp,
      playheadMs,
      LIVE_REPLAY_TRACK_STEP_MS
    );
    const signature = `h${histVer}|t${trackVer}|${trailFingerprint}`;
    if (signature === lastReplayTrailSignature) return;
    lastReplayTrailSignature = signature;

    const groupsToUpdate: Array<{ sourceId: number; sourceName: string; color: string; data: any[] }> =
      [];

    for (const sourceId of selected) {
      const replayTrail = replayTrailBySource.get(sourceId);
      if (!replayTrail || replayTrail.length === 0) continue;

      const renderData = capReplayTrailLength(
        buildDensifiedReplayTrack(
          replayTrail,
          windowStartMs,
          playheadMs,
          LIVE_REPLAY_TRACK_STEP_MS,
          getTimestamp,
          getCoordinates,
          liveInterpolateOptions
        ) as any[]
      );
      if (renderData.length === 0) continue;

      const samplePoint = renderData[renderData.length - 1];
      const pointSourceName = (samplePoint as { source_name?: string })?.source_name;
      const { sourceName, color } = resolveSourceStyle(sourceId, pointSourceName);

      groupsToUpdate.push({ sourceId, sourceName, color, data: renderData });
      lastProcessedTimestamps.set(sourceId, getTimestamp(renderData[renderData.length - 1]));
    }

    renderPaths(groupsToUpdate);
  };

  const replayFilterOptions = (effectiveTime: Date | null | undefined) =>
    liveReplayFilteredDataOptions(effectiveTime, props.replayTimeWindowMin);

  const resolveSourceStyle = (
    sourceId: number,
    pointSourceName?: string
  ): { sourceName: string; color: string } => {
    const styled = props.sourceStyles?.get(sourceId);
    if (styled) return styled;
    const sources = sourcesStore.sources();
    let sourceInfo: any = null;
    if (pointSourceName) {
      sourceInfo = sources.find(
        (s: any) =>
          s.source_name &&
          String(s.source_name).toLowerCase() === String(pointSourceName).toLowerCase()
      );
    }
    if (!sourceInfo) {
      sourceInfo = sources.find((s: any) => Number(s.source_id) === Number(sourceId));
    }
    const sourceName = sourceInfo?.source_name || pointSourceName || `Source ${sourceId}`;
    const color = resolveTrackColor(sourceName, sourceInfo?.color || sourcesStore.getSourceColor(sourceName) || "#1f77b4");
    return { sourceName, color };
  };

  // Helper to check if point has valid coordinates
  const hasValidCoordinates = (point: any): boolean => {
    return getCoordinates(point) !== null;
  };

  /** Split polyline at time gaps and implausible distance jumps (prevents GPS spokes). */
  const splitTrackSegmentByDistance = (points: any[]): any[][] => {
    if (points.length <= 1) return [points];

    const segments: any[][] = [];
    let current: any[] = [points[0]];

    for (let i = 1; i < points.length; i++) {
      const prev = points[i - 1];
      const pt = points[i];
      const prevCoords = getCoordinates(prev);
      const coords = getCoordinates(pt);
      const ts = getTimestamp(pt);
      const lastTs = getTimestamp(prev);

      let split = false;
      if (prevCoords && coords && ts > lastTs) {
        const elapsedSec = Math.max(0.001, (ts - lastTs) / 1000);
        const distance = distanceMeters(prevCoords, coords);
        const maxDistance =
          LIVE_TRACK_JUMP_MARGIN_M + (LIVE_TRACK_MAX_JUMP_SPEED_KPH / 3.6) * elapsedSec;
        split = distance > maxDistance;
      }

      if (split) {
        if (current.length > 0) segments.push(current);
        current = [pt];
      } else {
        current.push(pt);
      }
    }

    if (current.length > 0) segments.push(current);
    return segments;
  };

  const splitTrackSegments = (points: any[]): any[][] => {
    const timeSegments = splitTrackPolylineAtGaps(points, getTimestamp, GAP_THRESHOLD_MS);
    const result: any[][] = [];
    for (const seg of timeSegments) {
      result.push(...splitTrackSegmentByDistance(seg));
    }
    return result;
  };

  const makeTrackLineGenerator = () =>
    d3!.line<any>()
      .x((d: any) => {
        const lat = d.__renderLat ?? getCoordinates(d)?.lat;
        const lng = d.__renderLng ?? getCoordinates(d)?.lng;
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return 0;
        return props.map!.project([lng, lat]).x;
      })
      .y((d: any) => {
        const lat = d.__renderLat ?? getCoordinates(d)?.lat;
        const lng = d.__renderLng ?? getCoordinates(d)?.lng;
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return 0;
        return props.map!.project([lng, lat]).y;
      })
      .defined((d: any) => {
        if (Number.isFinite(d.__renderLat) && Number.isFinite(d.__renderLng)) return true;
        return hasValidCoordinates(d);
      });

  /** Linear polyline; gap splits become separate moveto subpaths (no line across gaps). */
  const setTrackPathData = (path: any, points: any[], lineGenerator: ReturnType<typeof makeTrackLineGenerator>) => {
    const segments = splitTrackSegments(points).filter((seg) => seg.length > 1);
    if (segments.length === 0) {
      path.attr("d", "");
      return;
    }
    path.attr(
      "d",
      segments
        .map((seg) => lineGenerator(seg))
        .filter(Boolean)
        .join("")
    );
  };

  // Update path positions when map moves/zooms (and every frame in replay — same as boats)
  const updatePathPositions = () => {
    if (!trackOverlay || !props.map || !d3) return;

    const lineGenerator = makeTrackLineGenerator();
    const pathGroups = trackOverlay.selectAll<SVGGElement, unknown>("g.track-path-group");

    pathGroups.each(function () {
      const group = d3!.select(this);
      const path = group.select<SVGPathElement>("path.track-path");
      if (path.empty()) return;
      const sourceId = Number(group.attr("data-source-id"));
      const data = lastRenderedTrackBySourceId.get(sourceId);
      if (!data || data.length === 0) return;
      setTrackPathData(path, data, lineGenerator);
    });
  };

  // Create SVG overlay
  const createSVGOverlay = () => {
    if (!d3) return;
    
    debug('[LiveTrackLayer] createSVGOverlay called', {
      hasMap: !!props.map,
      isStyleLoaded: props.map?.isStyleLoaded(),
      selectedSources: Array.from(props.selectedSourceIds || [])
    });
    
    if (!props.map) {
      warn('[LiveTrackLayer] No map provided, cannot initialize');
      return;
    }
    
    // Check if map is loaded
    if (!props.map.isStyleLoaded()) {
      debug('[LiveTrackLayer] Map style not loaded, waiting...');
      props.map.once('styledata', () => {
        debug('[LiveTrackLayer] Map style loaded, retrying createSVGOverlay');
        createSVGOverlay();
      });
      return;
    }
    
    // Remove only this map instance's overlay
    const existingContainer = overlayContainer || props.map.getCanvasContainer();
    if (existingContainer) {
      d3.select(existingContainer).selectAll(".live-track-overlay").remove();
    }
    
    // Create SVG overlay - attach to map canvas container
    let container: HTMLElement | null = null;
    try {
      container = props.map.getCanvasContainer() as HTMLElement;
    } catch (e) {
      debug('[LiveTrackLayer] getCanvasContainer failed, trying .map element', e);
    }
    
    if (!container) {
      try {
        container = props.map.getContainer() as HTMLElement;
      } catch (e) {
        debug('[LiveTrackLayer] getContainer fallback failed', e);
      }
    }
    
    if (!container) {
      warn('[LiveTrackLayer] Container not found');
      return;
    }
    
    const mapRect = container.getBoundingClientRect();
    const clientWidth = container.clientWidth || mapRect.width;
    const clientHeight = container.clientHeight || mapRect.height;
    const width = mapRect.width || clientWidth || 0;
    const height = mapRect.height || clientHeight || 0;
    
    overlayContainer = container;
    svg = d3.select(container)
      .append("svg")
      .attr("class", "live-track-overlay")
      .attr("width", width || 800)
      .attr("height", height || 600)
      .style("position", "absolute")
      .style("top", "0")
      .style("left", "0")
      .style("width", "100%")
      .style("height", "100%")
      .style("pointer-events", "none")
      .style("z-index", "100");
    
    trackOverlay = svg.append("g").attr("class", "live-track-layer");
    
    // Mark as ready so effects can react
    setSvgReady(true);
    setTrackOverlayReady(true);
    
    // Add resize handler to update SVG dimensions
    const resizeHandler = () => {
      const newRect = container?.getBoundingClientRect();
      if (svg && newRect) {
        svg.attr("width", newRect.width).attr("height", newRect.height);
      }
      // Update path positions when container resizes
      updatePathPositions();
    };
    
    window.addEventListener('resize', resizeHandler);
    
    // Add map move/zoom handlers to update path positions
    mapUpdateHandler = () => {
      updatePathPositions();
    };
    
    props.map.on('move', mapUpdateHandler);
    props.map.on('zoom', mapUpdateHandler);
    props.map.on('rotate', mapUpdateHandler);
    props.map.on('pitch', mapUpdateHandler);
    props.map.on('render', mapUpdateHandler);
    
    debug('[LiveTrackLayer] SVG overlay created');
  };

  // Render paths using enter/append/exit pattern
  const renderPaths = (groups: Array<{ sourceId: number; sourceName: string; color: string; data: any[] }>) => {
    if (!trackOverlay || !props.map || !d3) return;
    
    // Use enter/append/exit pattern for path groups
    const pathGroups = trackOverlay.selectAll<SVGGElement, any>("g.track-path-group")
      .data(groups, (d: any) => d.sourceId);
    
    // Exit: remove groups for deselected sources
    pathGroups.exit().remove();

    const activeSourceIds = new Set(groups.map((g) => g.sourceId));
    for (const id of lastRenderedTrackBySourceId.keys()) {
      if (!activeSourceIds.has(id)) lastRenderedTrackBySourceId.delete(id);
    }
    for (const g of groups) {
      lastRenderedTrackBySourceId.set(g.sourceId, g.data);
    }
    
    // Enter: create new groups for new sources
    const pathGroupsEnter = pathGroups.enter()
      .append("g")
      .attr("class", "track-path-group")
      .attr("data-source-id", (d: any) => d.sourceId);
    
    // Create path in each new group
    pathGroupsEnter.append("path")
      .attr("class", "track-path")
      .style("fill", "none")
      .style("stroke-width", "1px");
    
    // Update: merge enter and update selections
    const pathGroupsUpdate = pathGroupsEnter.merge(pathGroups);
    
    // Update path data and styling
    pathGroupsUpdate.each(function(groupData) {
      const group = d3.select(this);
      // Guard: only one path element per source (prevents stacked fan artifacts)
      group.selectAll("path.track-path").filter((_d, i) => i > 0).remove();
      const path = group.select<SVGPathElement>("path.track-path");
      
      if (!groupData.data || groupData.data.length === 0) {
        path.attr("d", "");
        return;
      }

      const lineGenerator = makeTrackLineGenerator();
      setTrackPathData(path, groupData.data, lineGenerator);
      path.style("stroke", groupData.color);
    });
  };

  // Initialize SVG overlay
  onMount(async () => {
    // Load d3 and mapbox-gl dynamically
    d3 = await import('d3');
    
    debug('[LiveTrackLayer] onMount called', {
      hasMap: !!props.map,
      mapLoaded: props.map?.loaded(),
      selectedSources: Array.from(props.selectedSourceIds || [])
    });
    
    if (!props.map) {
      warn('[LiveTrackLayer] No map provided, cannot initialize');
      return;
    }

    // Wait for map to be fully loaded
    if (props.map.loaded()) {
      debug('[LiveTrackLayer] Map already loaded, creating SVG overlay');
      createSVGOverlay();
    } else {
      debug('[LiveTrackLayer] Waiting for map to load...');
      props.map.once('load', () => {
        debug('[LiveTrackLayer] Map loaded, creating SVG overlay');
        createSVGOverlay();
      });
    }
  });

  // INITIAL LOAD: Load historical data once when component mounts or sources change
  // Track SVG/trackOverlay state to trigger re-run when they become available
  createEffect(() => {
    const selected = props.selectedSourceIds || new Set<number>();
    // Re-run when history/WS data arrives (initial load may have started before history was ready)
    const _historicalVersion = streamingStore.getHistoricalDataVersion()();
    const _liveAppendVersion = streamingStore.getLiveDataAppendVersion()();
    void _historicalVersion;
    void _liveAppendVersion;
    // Use reactive signals to track when SVG is ready
    const overlayReady = trackOverlayReady();
    const svgIsReady = svgReady();

    if (!overlayReady || !svgIsReady) {
      return;
    }

    // Reset initialization only when the selected source set actually changes
    const currentSourceIds = sourceSelectionKey(selected);
    if (currentSourceIds !== lastSelectedSourceIdsKey && hasInitialized) {
      hasInitialized = false;
      lastRedisLoadSourceKey = '';
      lastProcessedTimestamps.clear();
    }
    
    if (selected.size === 0) {
      if (hasInitialized) {
        renderPaths([]);
        hasInitialized = false;
        lastProcessedTimestamps.clear();
        lastSelectedSourceIdsKey = '';
      }
      return;
    }

    // Only do initial load once per source selection
    if (hasInitialized) {
      return;
    }

    // Replay live map: track polyline rebuilt each boat-clock tick from catalog
    if (props.replayMode) {
      commitInitialLoadComplete(selected);
      return;
    }

    // CRITICAL: Load initial history if not already loaded
    // Check if streamingStore has data for these sources (use raw data check - we just need to know if any data exists)
    const existingDataMap = streamingStore.getFilteredData(
      selected,
      props.replayMode ? replayFilterOptions(null) : undefined
    );
    const hasExistingData = Array.from(existingDataMap.values()).some(data => data.length > 0);
    
    if (!hasExistingData) {
      const currentSourceKey = sourceSelectionKey(selected);
      // Avoid infinite loop: if we already triggered a history load for this source set and still no data, mark initialized
      if (lastRedisLoadSourceKey === currentSourceKey) {
        commitInitialLoadComplete(selected);
        return;
      }
      lastRedisLoadSourceKey = currentSourceKey;

      const currentTimeWindow = Number(timeWindow());
      // The live history slice must end at now; selectedTime can be old before timeline init.
      const endTime = Date.now();
      
      // Calculate minutes for the API call
      // If timeWindow is 0, fetch all available data (24 hours)
      // If timeWindow > 0, fetch data for that specific window
      const minutes = currentTimeWindow > 0 ? currentTimeWindow : 0;
      
      streamingStore.loadInitialDataFromRedis(selected, minutes, endTime)
        .catch((err) => {
          warn('[LiveTrackLayer] ❌ Failed to load initial history:', err);
        });
      // Return early - will re-run when data is loaded (or when version bumps with empty result)
      return;
    } else {
      lastRedisLoadSourceKey = ''; // Clear so future source changes can trigger a history load again
    }

    // Get ALL raw data from streamingStore for initial load (includes fetched history)
    // NOTE: For initial load, we want to show ALL historical tracks, not filtered by time window
    // Time window filtering will be applied in the incremental update effect when playing
    // Use getRawData() to bypass time window filtering and show complete history
    const filteredDataMap = streamingStore.getRawData(selected);
    
    // Combine all source data
    const accumulatingData: any[] = [];
    for (const [sourceId, data] of filteredDataMap.entries()) {
      accumulatingData.push(...data);
    }
    
    // Also include historicalData from props if provided (only enabled sources — props may lag one frame)
    const historicalData = props.historicalData;
    if (Array.isArray(historicalData) && historicalData.length > 0) {
      const existingTimestamps = new Set(accumulatingData.map(d => getTimestamp(d)));
      for (const point of historicalData) {
        const sid = point?.source_id;
        if (!Number.isFinite(sid) || !selected.has(Number(sid))) {
          continue;
        }
        const ts = getTimestamp(point);
        if (ts > 0 && !existingTimestamps.has(ts)) {
          accumulatingData.push(point);
        }
      }
    }

    // If no data yet, mark as initialized anyway so incremental updates can work
    if (accumulatingData.length === 0) {
      commitInitialLoadComplete(selected);
      return;
    }

    const groupsBySource = new Map<number, any[]>();
    for (const point of accumulatingData) {
      const sourceId = point.source_id;
      if (!sourceId || !selected.has(sourceId) || !hasValidCoordinates(point)) {
        continue;
      }
      if (!groupsBySource.has(sourceId)) {
        groupsBySource.set(sourceId, []);
      }
      groupsBySource.get(sourceId)!.push(point);
    }

    const groupsToUpdate: Array<{ sourceId: number; sourceName: string; color: string; data: any[] }> = [];
    for (const [sourceId, points] of groupsBySource.entries()) {
      const samplePoint = points[0];
      const pointSourceName = samplePoint?.source_name;
      const { sourceName, color } = resolveSourceStyle(sourceId, pointSourceName);

      const sortedData = [...points].sort((a, b) => getTimestamp(a) - getTimestamp(b));

      if (sortedData.length > 0) {
        groupsToUpdate.push({
          sourceId: sourceId,
          sourceName: sourceName,
          color: color,
          data: sortedData
        });
        
        // Track last processed timestamp for this source
        const lastTimestamp = getTimestamp(sortedData[sortedData.length - 1]);
        lastProcessedTimestamps.set(sourceId, lastTimestamp);
      }
    }

    // Initial load: Render all historical data
    if (groupsToUpdate.length > 0) {
      renderPaths(groupsToUpdate);
    }
    commitInitialLoadComplete(selected);
  });

  // REPLAY LIVE MAP: track polylines refresh on slow history/store lane (not each WebSocket tick).
  createEffect(() => {
    if (!props.replayMode) return;
    if (!trackOverlayReady() || !svgReady()) return;

    const replayWindowMin = props.replayTimeWindowMin ?? 0;
    if (replayWindowMin <= 0) return;

    const selected = props.selectedSourceIds || new Set<number>();
    const histVer = streamingStore.getHistoricalDataVersion()();
    const trackVer = streamingStore.getTrackDataVersion()();
    void histVer;
    void trackVer;
    if (isPlaying()) {
      void smoothPlaybackTimeForBoat();
    } else {
      void smoothPlaybackTimeForTrack();
    }

    if (selected.size === 0) {
      resetReplayTrailCaches();
      if (trackOverlay) renderPaths([]);
      return;
    }

    const playing = isPlaying();
    const playhead = playing ? smoothPlaybackTimeForBoat() : smoothPlaybackTimeForTrack();
    const playheadMs = playhead instanceof Date ? playhead.getTime() : 0;
    if (!isLiveReplayPlayheadReady(playheadMs)) return;

    renderReplayTracksFromBoatClock(selected, playhead, histVer, trackVer);
  });

  // INCREMENTAL UPDATES: Append new websocket data points as they arrive
  // Also handles initial load if no historical data was available
  // Also reacts to brush selection changes to filter map tracks
  createEffect(() => {
    const selected = props.selectedSourceIds || new Set<number>();
    const currentlyPlaying = isPlaying();
    const replayWindowMin = props.replayTimeWindowMin ?? 0;
    const effectiveTime =
      props.replayMode && replayWindowMin > 0
        ? smoothPlaybackTimeForTrack()
        : (props.effectivePlaybackTime ?? null);
    const currentTime =
      props.replayMode && replayWindowMin > 0
        ? smoothPlaybackTimeForTrack()
        : currentlyPlaying && effectiveTime instanceof Date
          ? effectiveTime
          : selectedTime();
    const timeForFilter = effectiveTime ?? currentTime;
    const framePoints = props.replayMode ? props.framePoints : undefined;
    void framePoints;
    void props.replayTimeWindowMin;
    const _historicalVersion = streamingStore.getHistoricalDataVersion()();
    const _liveAppendVersion = streamingStore.getLiveDataAppendVersion()();
    void _historicalVersion;
    void _liveAppendVersion;
    void incrementalThrottleEpoch();

    const throttleMs = props.replayMode
      ? LIVE_TRACK_REPLAY_THROTTLE_MS
      : props.inSplitView
        ? SPLIT_VIEW_THROTTLE_MS
        : LIVE_TRACK_THROTTLE_MS;
    if (lastIncrementalUpdateTime > 0 && !props.replayMode) {
      const now = Date.now();
      const elapsed = now - lastIncrementalUpdateTime;
      if (elapsed < throttleMs) {
        if (!incrementalThrottleTimer) {
          incrementalThrottleTimer = setTimeout(() => {
            incrementalThrottleTimer = null;
            setIncrementalThrottleEpoch((v) => v + 1);
          }, throttleMs - elapsed);
        }
        return;
      }
    }

    // Watch for brush selection changes (reactive) — skipped in replay mode (live explore map)
    const currentRange = props.replayMode ? [] : selectedRange();
    const currentRanges = props.replayMode ? [] : selectedRanges();
    
    if (!trackOverlay || !svg) {
      return;
    }

    if (selected.size === 0) {
      if (hasInitialized) {
        renderPaths([]);
      }
      return;
    }

    // When paused (isPlaying = false), do not update from websocket data
    // Only update when selectedTime changes manually or brush selection changes.
    // Replay live map: skip — boats freeze at smoothPlaybackTimeForBoat; paused branch used selectedTime and lagged behind.
    if (!currentlyPlaying && hasInitialized && !props.replayMode) {
      // Still allow manual selectedTime updates to work
      // Re-render tracks based on selectedTime
      // IMPORTANT: Apply brush selection to filter map tracks based on timeseries brush
      // This effect will re-run when brush selection changes (currentRange/currentRanges are tracked above)
      // NOTE: We intentionally do NOT watch newDataMap here when paused - only update on selectedTime/brush changes
      const filteredDataMap = streamingStore.getFilteredData(
        selected,
        props.replayMode
          ? replayFilterOptions(effectiveTime)
          : {
              selectedRange: currentRange,
              selectedRanges: currentRanges,
              effectivePlaybackTime: effectiveTime,
            }
      );
      const rawDataMap = props.replayMode ? streamingStore.getRawData(selected) : null;
      const sources = sourcesStore.sources();
      
      // Build groups from all current data
      // If there's a brush selection, show all data within the brush range (from start_time to end_time)
      // If there's no brush selection, filter data to only show up to selectedTime
      const hasBrushSelection = currentRange && currentRange.length > 0;
      const groupsBySource = new Map<number, any[]>();
      
      if (hasBrushSelection) {
        // Brush selection exists - get raw data and filter to brush range (start_time to end_time)
        const rawDataMap = streamingStore.getRawData(selected);
        const brushRange = currentRange[0]; // Get the first (and typically only) brush range
        if (brushRange && brushRange.start_time && brushRange.end_time) {
          const startTime = brushRange.start_time instanceof Date 
            ? brushRange.start_time.getTime() 
            : new Date(brushRange.start_time).getTime();
          const endTime = brushRange.end_time instanceof Date 
            ? brushRange.end_time.getTime() 
            : new Date(brushRange.end_time).getTime();
          
          for (const [sourceId, data] of rawDataMap.entries()) {
            if (selected.has(sourceId) && data.length > 0) {
              // Filter data to brush range (start_time to end_time)
              const filteredData = data.filter(p => {
                const ts = getTimestamp(p);
                return ts >= startTime && ts <= endTime;
              });
              if (filteredData.length > 0) {
                groupsBySource.set(sourceId, filteredData);
              }
            }
          }
        }
      } else {
        // No brush selection - use filtered data and filter to timeForFilter when paused
        for (const [sourceId, data] of filteredDataMap.entries()) {
          if (selected.has(sourceId) && data.length > 0) {
            // Filter data to only show up to timeForFilter (boat position when effectiveTime provided)
            if (timeForFilter instanceof Date) {
              const currentTimeMs = timeForFilter.getTime();
              const filteredData = data.filter(p => {
                const ts = getTimestamp(p);
                return ts <= currentTimeMs;
              });
              if (filteredData.length > 0) {
                groupsBySource.set(sourceId, filteredData);
              }
            } else {
              groupsBySource.set(sourceId, data);
            }
          }
        }
      }
      
      // Create groups for rendering
      const groupsToUpdate: Array<{ sourceId: number; sourceName: string; color: string; data: any[] }> = [];
      for (const [sourceId, points] of groupsBySource.entries()) {
        // Ensure sourcesStore is ready and find matching source
        if (!sourcesStore.isReady()) {
          debug('[LiveTrackLayer] ⚠️ sourcesStore not ready when assigning colors (paused mode), using default');
        }
        // Prioritize matching by source_name from data (since boat labels use this and it's more reliable)
        let sourceInfo: any = null;
        const samplePoint = points[0];
        const pointSourceName = samplePoint?.source_name;
        
        if (pointSourceName) {
          // First try: match by source_name from data point (most reliable)
          sourceInfo = sources.find((s: any) => 
            s.source_name && String(s.source_name).toLowerCase() === String(pointSourceName).toLowerCase()
          );
        }
        
        // Fallback: try matching by source_id
        if (!sourceInfo) {
          sourceInfo = sources.find((s: any) => Number(s.source_id) === Number(sourceId));
        }
        
        const sourceName = sourceInfo?.source_name || pointSourceName || `Source ${sourceId}`;
        const color = resolveTrackColor(sourceName, sourceInfo?.color || sourcesStore.getSourceColor(sourceName) || '#1f77b4');

        // Debug color assignment (only log if there's an issue)
        if (!sourceInfo || !sourceInfo.color) {
          debug('[LiveTrackLayer] 🎨 Track color resolved (paused mode)', {
            sourceId,
            sourceName,
            color,
            pointSourceName,
            foundBySourceName: pointSourceName ? !!sources.find((s: any) => 
              s.source_name && String(s.source_name).toLowerCase() === String(pointSourceName).toLowerCase()
            ) : false,
            foundBySourceId: !sourceInfo ? !!sources.find((s: any) => Number(s.source_id) === Number(sourceId)) : false,
            usedFallback: !sourceInfo || !sourceInfo.color,
            pointCount: points.length
          });
        }
        
        // Filter to valid coordinates and sort
        const validPoints = points.filter(p => hasValidCoordinates(p));
        let sortedData = validPoints;
        const interpCatalog = props.replayMode
          ? (rawDataMap?.get(sourceId) ?? points)
          : points;
        sortedData = buildTrackPathToPlayhead(validPoints, interpCatalog, timeForFilter);
        if (sortedData.length > 0) {
          groupsToUpdate.push({
            sourceId: sourceId,
            sourceName: sourceName,
            color: color,
            data: sortedData
          });
        }
      }
      
      // Re-render paths based on selectedTime
      if (groupsToUpdate.length > 0) {
        if (props.inSplitView) lastIncrementalUpdateTime = Date.now();
        renderPaths(groupsToUpdate);
      } else if (selected.size > 0) {
        if (props.inSplitView) lastIncrementalUpdateTime = Date.now();
        renderPaths([]);
      }
      return; // Don't process websocket updates when paused
    }

    // Watch for new websocket data updates (reactive signal) - only when playing
    const newDataMap = streamingStore.getNewData()();

    // If not initialized yet and we have new data, do initial load with new data
    if (!hasInitialized && newDataMap && newDataMap.size > 0) {
      const filteredDataMap = streamingStore.getFilteredData(
        selected,
        props.replayMode
          ? replayFilterOptions(effectiveTime)
          : { effectivePlaybackTime: effectiveTime }
      );
      
      const accumulatingData: any[] = [];
      for (const [sourceId, data] of filteredDataMap.entries()) {
        accumulatingData.push(...data);
      }
      
      if (accumulatingData.length > 0) {
        const sources = sourcesStore.sources();
        const groupsBySource = new Map<number, any[]>();
        
        for (const point of accumulatingData) {
          const sourceId = point.source_id;
          if (!sourceId || !selected.has(sourceId) || !hasValidCoordinates(point)) {
            continue;
          }
          
          if (!groupsBySource.has(sourceId)) {
            groupsBySource.set(sourceId, []);
          }
          groupsBySource.get(sourceId)!.push(point);
        }
        
        const groupsToUpdate: Array<{ sourceId: number; sourceName: string; color: string; data: any[] }> = [];
        for (const [sourceId, points] of groupsBySource.entries()) {
          // Try to find source by source_id first
          let sourceInfo = sources.find((s: any) => Number(s.source_id) === Number(sourceId));
          
          // If not found, try using source_name from the data points as fallback
          if (!sourceInfo && points.length > 0) {
            const samplePoint = points[0];
            const pointSourceName = samplePoint?.source_name;
            if (pointSourceName) {
              sourceInfo = sources.find((s: any) => 
                s.source_name && String(s.source_name).toLowerCase() === String(pointSourceName).toLowerCase()
              );
            }
          }
          
          const sourceName = sourceInfo?.source_name || points[0]?.source_name || `Source ${sourceId}`;
          const color = resolveTrackColor(sourceName, sourceInfo?.color || sourcesStore.getSourceColor(sourceName) || '#1f77b4');

          const sortedData = [...points].sort((a, b) => getTimestamp(a) - getTimestamp(b));
          
          if (sortedData.length > 0) {
            groupsToUpdate.push({
              sourceId: sourceId,
              sourceName: sourceName,
              color: color,
              data: sortedData
            });
            
            const lastTimestamp = getTimestamp(sortedData[sortedData.length - 1]);
            lastProcessedTimestamps.set(sourceId, lastTimestamp);
          }
        }
        
        if (groupsToUpdate.length > 0) {
          if (props.inSplitView) lastIncrementalUpdateTime = Date.now();
          renderPaths(groupsToUpdate);
          commitInitialLoadComplete(selected);
          return; // Done with initial load
        }
      }
      // WebSocket has new data but store has none (e.g. the history fetch just returned empty and overwrote) - mark initialized so incremental path runs next time
      if (!hasInitialized && newDataMap && newDataMap.size > 0) {
        commitInitialLoadComplete(selected);
      }
    }
    
    if (!hasInitialized) {
      return; // Still waiting for initial data
    }

    // Replay live map: trail + redraw driven by boat clock effect, not WS incremental path
    if (props.replayMode) {
      return;
    }

    // When playing: always re-render from current store data so tracks update as effectivePlaybackTime advances.
    // Do NOT return when newDataMap is empty - it gets cleared by LiveMultiBoatLayer after 100ms, so we must
    // still redraw from getFilteredData (which uses historicalData) so the track extends smoothly to the boat.
    // When newDataMap has content we also get a fresh render; when only time advanced we still need to render.

    // For incremental updates, get all current data for each source and re-render
    // IMPORTANT: Apply brush selection and effectivePlaybackTime for time-window sync with boat
    const filteredDataMap = streamingStore.getFilteredData(
      selected,
      props.replayMode
        ? replayFilterOptions(effectiveTime)
        : {
            selectedRange: currentRange,
            selectedRanges: currentRanges,
            effectivePlaybackTime: effectiveTime,
          }
    );
    
    // Build groups from all current data (historical + new)
    const groupsBySource = new Map<number, any[]>();
    for (const [sourceId, data] of filteredDataMap.entries()) {
      if (selected.has(sourceId) && data.length > 0) {
        groupsBySource.set(sourceId, data);
      }
    }
    
    // Create groups for rendering
    const groupsToUpdate: Array<{ sourceId: number; sourceName: string; color: string; data: any[] }> = [];
    for (const [sourceId, points] of groupsBySource.entries()) {
      const samplePoint = points[0];
      const pointSourceName = samplePoint?.source_name;
      const { sourceName, color } = resolveSourceStyle(sourceId, pointSourceName);

      const validPoints = points.filter(p => hasValidCoordinates(p));
      const sortedData = buildTrackPathToPlayhead(validPoints, validPoints, effectiveTime);
      if (sortedData.length > 0) {
        groupsToUpdate.push({
          sourceId: sourceId,
          sourceName: sourceName,
          color: color,
          data: sortedData
        });
        
        const lastTimestamp = getTimestamp(sortedData[sortedData.length - 1]);
        lastProcessedTimestamps.set(sourceId, lastTimestamp);
      }
    }
    
    // Re-render all paths (enter/append/exit will handle updates efficiently)
    if (groupsToUpdate.length > 0) {
      const sortedSel = [...selected].sort((a, b) => a - b);
      const timeMs = timeForFilter instanceof Date ? timeForFilter.getTime() : 0;
      let renderSig = `${currentlyPlaying}|${_historicalVersion}|${_liveAppendVersion}|${timeMs}|${sortedSel.join(',')}`;
      for (const g of groupsToUpdate) {
        const lastPt = g.data.length > 0 ? g.data[g.data.length - 1] : null;
        const lastTs = lastPt ? getTimestamp(lastPt) : 0;
        const c = lastPt ? getCoordinates(lastPt) : null;
        renderSig += `|${g.sourceId}:${g.data.length}:${lastTs}:${c?.lat ?? ""}:${c?.lng ?? ""}`;
      }
      if (renderSig === lastIncrementalRenderSignature) {
        return;
      }
      lastIncrementalRenderSignature = renderSig;

      lastIncrementalUpdateTime = Date.now();
      renderPaths(groupsToUpdate);
    } else if (selected.size > 0) {
      lastIncrementalUpdateTime = Date.now();
      renderPaths([]);
    }
  });

  // Cleanup on unmount
  onCleanup(() => {
    if (incrementalThrottleTimer) {
      clearTimeout(incrementalThrottleTimer);
      incrementalThrottleTimer = null;
    }
    // Remove SVG overlay
    if (d3 && overlayContainer) {
      d3.select(overlayContainer).selectAll(".live-track-overlay").remove();
      svg = null;
      trackOverlay = null;
      overlayContainer = null;
    }
    
    // Remove map event listeners
    if (props.map && mapUpdateHandler) {
      props.map.off('move', mapUpdateHandler);
      props.map.off('zoom', mapUpdateHandler);
      props.map.off('rotate', mapUpdateHandler);
      props.map.off('pitch', mapUpdateHandler);
      props.map.off('render', mapUpdateHandler);
      mapUpdateHandler = null;
    }
  });

  return null; // This component doesn't render JSX, it manages SVG rendering
}
